import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef, useState } from 'react'
import Autosuggest from 'react-autosuggest'
import { AiOutlineArrowRight } from '@react-icons/all-files/ai/AiOutlineArrowRight'
import { MdLocationOn } from '@react-icons/all-files/md/MdLocationOn'
import { MdMyLocation } from '@react-icons/all-files/md/MdMyLocation'
import { useSelector, useDispatch } from 'react-redux'

import { LanguageContext } from 'context/LanguageContext'
import { RouteConstants } from 'config/routes/routeConstants'
import { setIsAddressDeliverable } from 'modules/location/actions'
import { getPredictions } from 'modules/location/sagas'
import { debounce, isEmpty } from 'utils/appUtils'
import Util from 'utils/Util'
import { defaultLangConstants } from 'utils/defaultLangConstants'
import { useNavigate } from 'react-router-dom'

const effectiveDebounce = new debounce()

export const AddressBar = ({
    className,
    selectedAddress,
    styleChange,
    selectAddressFromList,
    routePostValidation,
    selectAddressFromRecentList,
    selectAddressFromSavedList,
    selectAddressFromGeoLocation,
    isPickup,
    id,
    isAddressValid,
    isRegistration,
    shouldShowLoader = false,
    addressCompInLandingPage = false,
    placeholder,
    setLoading = () => {},
    isGeoLocationEnabled = true,
    setLoader = () => {},
}) => {
    const { strings } = useContext(LanguageContext)

    // TODO: Recent address scenario needs to be handled
    const savedAddressList = useSelector(state => state.location.getIn(['savedAddresses']))?.toJS()
    const userProfile = useSelector(state => state.user.getIn(['userProfile']))
    const isLoading = useSelector(state => state.languageChange.get('isLoading'))
    const dispatch = useDispatch()
    const formatted_address = !isEmpty(selectedAddress) ? selectedAddress.formatted_address : ''
    const [value, setvalue] = useState(formatted_address)
    const [addressError, setAddressError] = useState(false)
    const navigate = useNavigate()
    const [multiSection, setMultisection] = useState(true)
    const multiSectionRef = useRef(multiSection)

    const getLocationSuggestion = {
        description: isLoading
            ? defaultLangConstants.LANDING_LOCATION_USE_CURRENT
            : strings.LANDING_LOCATION_USE_CURRENT,
        place_id: 'getLocation',
    }

    const defaultSuggestion = isGeoLocationEnabled
        ? [
              {
                  title: '',
                  suggestions: [getLocationSuggestion],
              },
          ]
        : []

    const defaultMultiSecSuggestions =
        userProfile !== null
            ? value !== ''
                ? defaultSuggestion
                : [
                      ...defaultSuggestion,
                      { title: 'Saved Address', suggestions: savedAddressList || [] },
                  ]
            : defaultSuggestion

    const [suggestions, setsuggestions] = useState(defaultMultiSecSuggestions)
    const [backupSuggestion, setBackUpSuggestion] = useState([])
    const [isLocationClick, setIsLocationClick] = useState(false)
    const suggestionsRef = useRef(suggestions)

    useEffect(() => {
        if (isEmpty(selectedAddress) && addressCompInLandingPage) {
            handleLocClick()
        }
    }, [])

    useEffect(() => {
        const currentValue = value
        setvalue(formatted_address)
        if (id) {
            document.getElementsByClassName(id)[0].addEventListener('keypress', function (e) {
                if (e.key === 'Enter') {
                    if (multiSectionRef.current) {
                        if (suggestionsRef.current[0].suggestions.length > 0) {
                            const suggestion = suggestionsRef.current[0].suggestions[0]
                            onSuggestionSelected(null, {
                                method: 'enter',
                                suggestion,
                                suggestionValue: suggestion.formatted_address,
                            })
                        }
                    } else if (suggestionsRef.current.length > 1) {
                        const suggestion = suggestionsRef.current[1]
                        onSuggestionSelected(null, {
                            method: 'enter',
                            suggestion,
                            suggestionValue: suggestion.description,
                        })
                    } else {
                        setAddressError(true)
                    }
                    e.stopPropagation()
                }
            })
        } else
            console.warn(
                'Enter key wont work if you wont supply the id as prop in addressbar component'
            )

        if (!isEmpty(selectedAddress) && Util.isAddressValid(selectedAddress)) {
            routePostValidation()
            setAddressError(false)
        } else if (isAddressValid && !isRegistration && (currentValue || isLocationClick)) {
            dispatch(setIsAddressDeliverable(false))
            routePostValidation()
        } else if (!isEmpty(selectedAddress) && !Util.isAddressValid(selectedAddress)) {
            setAddressError(true)
        }
    }, [formatted_address, isAddressValid])

    const getSuggestionValue = suggestion =>
        suggestion.place_id === getLocationSuggestion.place_id
            ? ''
            : suggestion.description || suggestion.formatted_address

    const renderSuggestion = suggestion => (
        <span>
            {suggestion.place_id === getLocationSuggestion.place_id ? (
                <MdMyLocation className={'currentlocation'} />
            ) : (
                <MdLocationOn className={'location'} />
            )}
            {suggestion.description || suggestion.nickname || suggestion.formatted_address}
        </span>
    )

    const onSuggestionsFetchRequested = ({ value }) => {
        if (value !== '' && value !== selectedAddress)
            effectiveDebounce(() => loadSuggestions(value), 300)
    }

    const onSuggestionsClearRequested = () => {
        setMultisection(true)
        multiSectionRef.current = true
        setsuggestions(defaultMultiSecSuggestions)
        suggestionsRef.current = defaultMultiSecSuggestions
    }

    const onChange = (event, { newValue }) => setvalue(newValue)

    const loadSuggestions = value => {
        getPredictions(value).then(res => {
            setMultisection(false)
            multiSectionRef.current = false
            if (isGeoLocationEnabled) {
                setsuggestions([getLocationSuggestion, ...res])
                suggestionsRef.current = [getLocationSuggestion, ...res]
            } else {
                setsuggestions([...res])
                suggestionsRef.current = [...res]
            }
            res.length > 0 ? setBackUpSuggestion([res[0]]) : setBackUpSuggestion([])
        })
    }

    const onSuggestionSelected = (e, { method, suggestion, suggestionValue }) => {
        // handle location click from dropdown
        setLoader(true)
        setLoading(true)
        if (suggestion.place_id === getLocationSuggestion.place_id) {
            handleLocClick(e)
            setvalue('')
            return 0
        }

        if (suggestion.formatted_address) {
            selectAddressFromSavedList(suggestion)
            return 0
        }
        setvalue(suggestionValue)
        selectAddressFromList(suggestion, method)
    }
    const renderSectionTitle = section =>
        section.suggestions.length > 0 ? <strong>{section.title}</strong> : null

    const getSectionSuggestions = section => section.suggestions

    const handleClickGo = () => {
        if ((Util.isAddressValid(selectedAddress) && !addressError) || isAddressValid) {
            navigate(RouteConstants.Home)
        } else if (backupSuggestion.length > 0 && !selectedAddress.formatted_address) {
            const suggestion = backupSuggestion[0]
            setAddressError(false)
            onSuggestionSelected(null, {
                method: 'click',
                suggestion,
                suggestionValue: suggestion.description,
            })
        } else {
            setAddressError(true)
        }
    }

    const handleLocClick = function (e) {
        if (!navigator.geolocation) {
            //Can notify user when has network
        } else {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    const { latitude } = position.coords
                    const { longitude } = position.coords
                    setIsLocationClick(true)
                    setLoading(true)
                    selectAddressFromGeoLocation({ latitude, longitude })
                },
                function error() {
                    if (shouldShowLoader) {
                        setLoading(false)
                    }
                }
            )
        }
        e && e.preventDefault()
    }

    const inputProps = {
        placeholder: placeholder
            ? placeholder
            : isPickup
              ? isLoading
                  ? defaultLangConstants.ADDRESS_PICKUP_PLACEHOLDER
                  : strings.ADDRESS_PICKUP_PLACEHOLDER
              : isLoading
                ? defaultLangConstants.addressBarPlaceholder
                : strings.addressBarPlaceholder,
        value: value || '',
        onChange,
        className: `address-form__enter ${id}`,
    }
    return (
        <div className={`baseWrapper ${className}`}>
            <div className={`address-form ${styleChange}`}>
                <div
                    className={`${'address-form__wrapper'}${' '}${
                        addressError ? 'wrapperError' : ''
                    } ${isGeoLocationEnabled ? null : 'noCurrentLocation'}`}
                >
                    <button
                        type={'button'}
                        onClick={handleLocClick}
                        className={'address-form__getloc'}
                        aria-label="current location"
                    >
                        <MdLocationOn className={'address-form__getloc-icon'} />
                    </button>
                    <Autosuggest
                        id={id}
                        multiSection={multiSection}
                        renderSectionTitle={renderSectionTitle}
                        getSectionSuggestions={getSectionSuggestions}
                        shouldRenderSuggestions={() => true}
                        onSuggestionSelected={onSuggestionSelected}
                        focusInputOnSuggestionClick
                        suggestions={suggestions}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={onSuggestionsClearRequested}
                        getSuggestionValue={getSuggestionValue}
                        renderSuggestion={renderSuggestion}
                        inputProps={inputProps}
                        aria-labelledby="enter address input field"
                    />
                </div>
                <button
                    type={'button'}
                    onClick={handleClickGo}
                    className={'address-form__setloc'}
                    aria-label="go"
                >
                    <AiOutlineArrowRight className={'address-form__setloc-icon'} />
                </button>
            </div>
            <div className={'errorMessage'}>
                {addressError && !isPickup && (
                    <p>
                        {isLoading ? defaultLangConstants.invalidAddress : strings.invalidAddress}
                    </p>
                )}
            </div>
        </div>
    )
}

AddressBar.propTypes = {
    className: PropTypes.string,
    selectedAddress: PropTypes.object,
    selectAddressFromList: PropTypes.func,
    selectAddressFromRecentList: PropTypes.func,
    selectAddressFromSavedList: PropTypes.func,
    routePostValidation: PropTypes.func,
    selectAddressFromGeoLocation: PropTypes.func,
    id: PropTypes.string,
    isRegistration: PropTypes.bool,
}

AddressBar.defaultProps = {
    selectedAddress: '',
    routePostValidation: () => {},
    className: '',
}

export default AddressBar
